/*
  Journey Editor Helpers

  Utility functions for working with journey editor state and data transformations.
*/
import type { JourneyTypes } from '@usealloy/typegen';
import { JourneyTypesMap } from '@usealloy/typegen';

import type { Outcome } from '~/dashboard/views/settings/outcomes/types';
import apiCalls from '~/helpers/apiCalls';
import type { JourneyDocument, JourneyWorkflowMetadata } from '../types';

/**
 * Extracts workflow metadata from journey document nodes.
 *
 * Scans all nodes in the document to find workflow references and creates
 * metadata entries for each one. Preserves existing metadata state
 * (initialization status, cached data, errors) while updating basic properties.
 *
 * @param doc - The journey document to scan for workflow nodes
 * @param previous - Existing workflow metadata to preserve state
 * @returns Updated workflow metadata mapping
 */
export function deriveJourneyWorkflows(
  doc: JourneyDocument | null,
  previous: Record<string, JourneyWorkflowMetadata> = {},
): Record<string, JourneyWorkflowMetadata> {
  if (!doc) return previous;

  const workflows: Record<string, JourneyWorkflowMetadata> = { ...previous };

  // Scan all nodes for workflow references
  Object.values(doc.nodes).forEach(node => {
    // Include all workflow-type nodes that reference workflows
    const isWorkflowNode = node.type === 'workflow'
      || node.type === 'start_router'
      || node.type === 'source_workflow'
      || node.type === 'reconciliation_workflow';

    if (isWorkflowNode) {
      const workflowToken = node.config?.workflow_token;

      if (workflowToken) {
        const existing = previous[workflowToken];

        // Extract version ID from node config (try multiple field names for compatibility)
        let workflowVersion: number | null = null;
        if (typeof node.config?.workflow_version_id === 'number') {
          workflowVersion = node.config.workflow_version_id;
        } else if (typeof node.config?.workflow_version === 'number') {
          workflowVersion = node.config.workflow_version;
        }

        // Create or update workflow metadata entry
        workflows[workflowToken] = {
          workflow_token: workflowToken,
          workflow_version: workflowVersion,
          workflow_type: node.config?.workflow_type ?? null,
          workflow_name: node.name ?? null,
          // Preserve existing state
          initialized: existing?.initialized ?? false,
          isInitializing: existing?.isInitializing ?? false,
          error: existing?.error ?? null,
          // Not caching full workflow data for now
          cachedWorkflowData: null,
          cachedWorkflowProps: null,
          outcomes: existing?.outcomes, // Preserve extracted outcomes
        };
      }
    }
  });

  return workflows;
}

const { settings: { getOutcomesNew: getOutcomes } } = apiCalls;

export const getOutcomeNameForJourneyAction = (outcome?: string | null): string => (
  (outcome ?? '').split(' ').join('_').toUpperCase()
);

export const APPLICATION_OUTCOMES = ['Approved', 'Denied'] as const;
export const ALERT_OUTCOMES = ['Dismissed', 'Suspicious'] as const;
export const MANUAL_REVIEW_OUTCOME = 'Manual Review';
export const WITHDRAWN_OUTCOME = 'Withdrawn';
export const APPLICATION_OUTCOMES_WITH_WITHDRAWN = [
  ...APPLICATION_OUTCOMES,
  WITHDRAWN_OUTCOME,
] as const;

/**
 * The standard Manual Review node should only ever have the default outcomes based on journey type
 * - Approved/Denied/Withdrawn (if relevant) for origination
 * - Dismissed/Suspicious for ongoing
 *
 * Custom outcomes for manual reviews can be configured using custom review nodes
 */
export const ALLOWED_APPLICATION_MANUAL_REVIEW_OUTCOMES = [
  ...APPLICATION_OUTCOMES,
  WITHDRAWN_OUTCOME,
] as const;
export const ALLOWED_ALERT_MANUAL_REVIEW_OUTCOMES = [...ALERT_OUTCOMES] as const;

export const OUTCOME_DISPLAY_NAMES_BY_OUTCOME = {
  APPROVED: 'Approved',
  DENIED: 'Denied',
  EXPIRED: 'Expired',
  MANUAL_REVIEW: MANUAL_REVIEW_OUTCOME,
  RETRIES_EXCEEDED: 'Retries Exceeded',
  RETRY: 'Retry',
} as const;

export const getAllowedJourneyOutcomes = async ({
  journeyType,
  typhonDomain,
}: {
  journeyType: JourneyTypes;
  typhonDomain: string;
}) => {
  const filterField = journeyType === JourneyTypesMap.APPLICATION
    ? 'is_application_outcome'
    : 'is_alert_outcome';

  /**
   * Journey Applications and Alerts store outcomes by name rather than DB id. So
   * we need to dedupe any custom outcomes from the default system outcomes,
   * case-insensitively.
   */
  const dedupeOutcomeArray = (
    journeyType === JourneyTypesMap.APPLICATION
      ? [...APPLICATION_OUTCOMES]
      : [...ALERT_OUTCOMES]
  ).map(name => name.toLowerCase());

  const { outcomes }: { outcomes: Outcome[] } = await getOutcomes({
    options: {
      numRowsPerPage: 250,
      q: {
        includeEventStatusOutcomes: true,
      },
    },
    typhonDomain,
  });

  const outcomesLists: {
    eventStatusOutcomes: Outcome[];
    journeyOutcomes: Outcome[];
    workflowAndActionOutcomes: Outcome[];
  } = outcomes.reduce((agg, outcome) => {
    if (outcome[filterField]) {
      if (
        outcome.is_system_outcome
        || (
          !outcome.archived
          && !dedupeOutcomeArray.includes(outcome.outcome.toLowerCase())
        )
      ) {
        agg.journeyOutcomes.push(outcome);
      }
    }

    if (outcome.is_event_status) {
      agg.eventStatusOutcomes.push(outcome);
    }

    if (outcome.is_workflow_outcome) {
      agg.workflowAndActionOutcomes.push(outcome);
    }

    return agg;
  }, {
    eventStatusOutcomes: [],
    journeyOutcomes: [],
    workflowAndActionOutcomes: [],
  });

  return outcomesLists;
};
